// src/components/calendar/CalendarShifts.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, CircularProgress
} from "@mui/material";
import axiosInstance from "../../config/axiosInstance";
import { StationContext } from "../context/StationContext";

const CalendarShifts = () => {
  const { selectedStation } = useContext(StationContext);
  const [turnos, setTurnos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerTurnos = async () => {
      if (!selectedStation?.id) { setTurnos([]); setCargando(false); return; }
      try {
        setCargando(true);
        setError(null);
        // Solo turnos locales de la estación seleccionada, habilitados
        const { data } = await axiosInstance.get(
          `/workshift?station=${encodeURIComponent(selectedStation.id)}&include_disabled=false`
        );
        const arr = Array.isArray(data) ? data : [];
        // normalizar claves posibles
        const norm = arr.map(t => ({
          id: t.id ?? t.workshift_id ?? t.ws_id,
          name: t.name ?? t.workshift_name ?? t.nombre,
          description: t.description ?? t.descripcion ?? "",
          is_principal: Boolean(t.is_principal ?? t.is_global ?? t.principal),
          enabled: t.enabled !== false, // por si no viene la clave
        })).filter(t => t.enabled);
        setTurnos(norm);
      } catch (e) {
        console.error(e);
        setError("Error al cargar los turnos de la estación.");
      } finally {
        setCargando(false);
      }
    };
    obtenerTurnos();
  }, [selectedStation?.id]);

  const { principales, noPrincipales } = useMemo(() => {
    const p = [], n = [];
    for (const t of turnos) (t.is_principal ? p : n).push(t);
    // ordenar por id asc y luego nombre
    const byOrder = (a, b) => (a.id ?? 0) - (b.id ?? 0) || String(a.name).localeCompare(String(b.name));
    return { principales: p.sort(byOrder), noPrincipales: n.sort(byOrder) };
  }, [turnos]);

  if (cargando) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Box sx={{ p: 2, textAlign: "center" }}><Typography variant="body1" color="error">{error}</Typography></Box>;
  }

  if (!selectedStation?.id) {
    return <Box sx={{ p: 2, textAlign: "center" }}><Typography variant="body2">Seleccioná una estación para ver sus turnos.</Typography></Box>;
  }

  return (
    <TableContainer component={Paper} sx={{ p: 2, overflowX: "hidden" }}>
      <Table size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow sx={{ height: 32 }}>
            <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>Turno</TableCell>
            <TableCell sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>Descripción</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* Sección: Principales */}
          <TableRow>
            <TableCell colSpan={2} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "text.secondary", py: 0.5 }}>
              Principales
            </TableCell>
          </TableRow>
          {principales.length === 0 ? (
            <TableRow><TableCell colSpan={2} sx={{ fontSize: "0.75rem", color: "text.disabled" }}>Sin turnos principales habilitados</TableCell></TableRow>
          ) : (
            principales.map(t => (
              <TableRow key={`p-${t.id}`} sx={{ height: 28 }}>
                <TableCell sx={{ fontSize: "0.75rem" }}>{t.name}</TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>{t.description}</TableCell>
              </TableRow>
            ))
          )}

          {/* Sección: No principales */}
          <TableRow>
            <TableCell colSpan={2} sx={{ fontSize: "0.72rem", fontWeight: 700, color: "text.secondary", pt: 1.5, pb: 0.5 }}>
              No principales
            </TableCell>
          </TableRow>
          {noPrincipales.length === 0 ? (
            <TableRow><TableCell colSpan={2} sx={{ fontSize: "0.75rem", color: "text.disabled" }}>Sin turnos no principales habilitados</TableCell></TableRow>
          ) : (
            noPrincipales.map(t => (
              <TableRow key={`n-${t.id}`} sx={{ height: 28 }}>
                <TableCell sx={{ fontSize: "0.75rem" }}>{t.name}</TableCell>
                <TableCell sx={{ fontSize: "0.75rem" }}>{t.description}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CalendarShifts;
